// SkuImportPanel — Excel/CSV file upload → AI preview → apply → undo (iter_143).
//
// Rendered inside the SkuChatDrawer as a mode switch. Users:
//   1. Upload .xlsx/.csv
//   2. Pick a sheet (if multiple) + optionally set a base rule name
//   3. AI analyzes and returns a diff plan (create / update / skip counts + per-row detail)
//   4. User clicks 適用 to apply — a batch record is stored with ops for undo
//   5. Within 24 hours, user can click 元に戻す to revert
//
// The panel keeps its state ephemeral (single active session per drawer); on
// close the drawer clears it. History of past imports is shown in a small
// list below the composer for quick undo access.
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  UploadSimple, ArrowsClockwise, CheckCircle, XCircle, Warning, ArrowClockwise,
  FileXls, Trash, ArrowRight, MagnifyingGlass, Sparkle,
} from '@phosphor-icons/react';
import { AiProgressBanner } from '@/components/AiProgressBanner';
import { formatDateTime } from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TARGET_LABELS = {
  rule: '品番ルール',
  category: '大分類',
  subcategory: '中分類',
  unit_code: 'ユニット図番',
  skip: 'スキップ',
};

const ACTION_TONE = {
  create: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  update: 'bg-amber-50 text-amber-700 border-amber-200',
  skip:   'bg-zinc-100 text-zinc-700 border-zinc-200',
  error:  'bg-rose-50 text-rose-700 border-rose-200',
};

const ACTION_LABEL = {
  create: '新規',
  update: '更新',
  skip: 'スキップ',
  error: 'エラー',
};

export default function SkuImportPanel({ onImported }) {
  const [uploading, setUploading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [filename, setFilename] = useState('');
  const [sheets, setSheets] = useState([]);
  const [chosenSheet, setChosenSheet] = useState('');
  const [baseName, setBaseName] = useState('');
  const [hint, setHint] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeStartedAt, setAnalyzeStartedAt] = useState(null);
  const [plan, setPlan] = useState(null); // {summary, counts, items}
  const [applying, setApplying] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [batches, setBatches] = useState([]);
  const [undoing, setUndoing] = useState(null);
  const fileInputRef = useRef(null);

  const loadBatches = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/sku-import/batches`, { withCredentials: true });
      setBatches(r.data || []);
    } catch {
      setBatches([]);
    }
  }, []);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  const resetSession = () => {
    setSessionId(null);
    setFilename('');
    setSheets([]);
    setChosenSheet('');
    setBaseName('');
    setHint('');
    setPlan(null);
    setExpandedRow(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setPlan(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/sku-import/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      setSessionId(r.data.session_id);
      setFilename(r.data.filename);
      setSheets(r.data.sheets || []);
      setChosenSheet(r.data.sheets?.[0]?.name || '');
      toast.success(`${r.data.sheets.length}シート読み込みました`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'アップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const analyze = async () => {
    if (!sessionId || !chosenSheet) return;
    setAnalyzing(true);
    setAnalyzeStartedAt(new Date().toISOString());
    setPlan(null);
    try {
      const r = await axios.post(
        `${API}/sku-import/preview`,
        { session_id: sessionId, sheet_name: chosenSheet, base_name: baseName, hint },
        { withCredentials: true, timeout: 120000 },
      );
      setPlan(r.data);
      toast.success(`AI 解析完了 — 新規 ${r.data.counts.create} / 更新 ${r.data.counts.update} / スキップ ${r.data.counts.skip}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI 解析に失敗しました');
    } finally {
      setAnalyzing(false);
      setAnalyzeStartedAt(null);
    }
  };

  const applyPlan = async () => {
    if (!sessionId || !plan) return;
    setApplying(true);
    try {
      const r = await axios.post(
        `${API}/sku-import/apply`,
        { session_id: sessionId },
        { withCredentials: true, timeout: 60000 },
      );
      const c = r.data.counts || {};
      toast.success(`取り込み完了 — 新規 ${c.created} / 更新 ${c.updated} / エラー ${c.errors}`);
      resetSession();
      await loadBatches();
      onImported?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || '取り込みに失敗しました');
    } finally {
      setApplying(false);
    }
  };

  const undo = async (batchId) => {
    setUndoing(batchId);
    try {
      const r = await axios.post(
        `${API}/sku-import/batches/${batchId}/undo`,
        {},
        { withCredentials: true, timeout: 60000 },
      );
      toast.success(`取り消しました — 削除 ${r.data.deleted} / 復元 ${r.data.restored}`);
      await loadBatches();
      onImported?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || '取り消しに失敗しました');
    } finally {
      setUndoing(null);
    }
  };

  const activeItems = plan?.items || [];
  const canApply = !!plan && (plan.counts?.create > 0 || plan.counts?.update > 0);

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-zinc-50" data-testid="sku-import-panel">
      {/* Upload area */}
      {!sessionId && (
        <div className="rounded-xl border-2 border-dashed border-indigo-200 bg-white p-6 text-center">
          <FileXls weight="duotone" size={40} className="mx-auto text-emerald-500" />
          <div className="mt-2 text-sm font-semibold text-zinc-800">Excel / CSV を選択</div>
          <div className="mt-1 text-xs text-zinc-500 leading-snug">
            .xlsx はシート単位で処理。行ごとに<br />
            <span className="font-semibold text-zinc-700">品番ルール / 大分類 / 中分類 / ユニット図番</span> を自動判別します。
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xlsm,.csv"
            onChange={onFileSelected}
            className="hidden"
            data-testid="sku-import-file-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="mt-4 bg-teal-700 hover:bg-teal-800"
            data-testid="sku-import-upload-btn"
          >
            {uploading ? (
              <><ArrowsClockwise className="animate-spin mr-1" size={14} />アップロード中…</>
            ) : (
              <><UploadSimple size={14} className="mr-1" />ファイルを選ぶ</>
            )}
          </Button>
        </div>
      )}

      {/* Sheet picker + AI analyze */}
      {sessionId && !plan && (
        <div className="rounded-xl border border-indigo-200 bg-white p-4 space-y-3" data-testid="sku-import-config">
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <FileXls size={14} className="text-emerald-500" />
            <span className="truncate font-mono">{filename}</span>
            <button onClick={resetSession} className="ml-auto text-rose-500 hover:underline" data-testid="sku-import-reset">
              リセット
            </button>
          </div>
          <div>
            <Label className="text-xs">シート選択</Label>
            <Select value={chosenSheet} onValueChange={setChosenSheet}>
              <SelectTrigger className="mt-0.5" data-testid="sku-import-sheet-select">
                <SelectValue placeholder="シートを選んでください" />
              </SelectTrigger>
              <SelectContent>
                {sheets.map((s) => (
                  <SelectItem key={s.name} value={s.name} data-testid={`sku-import-sheet-opt-${s.name}`}>
                    <span className="flex items-center gap-2">
                      <span className="font-semibold">{s.name}</span>
                      <span className="text-[10px] text-zinc-500">({s.row_count} 行)</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {chosenSheet && sheets.find((s) => s.name === chosenSheet)?.headers?.length > 0 && (
              <div className="mt-1 text-[10px] text-zinc-400 truncate">
                列: {sheets.find((s) => s.name === chosenSheet).headers.join(' / ')}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">ルール名（このシート全体）</Label>
            <Input
              value={baseName}
              onChange={(e) => setBaseName(e.target.value)}
              placeholder="例: ソルダリング / センスビー / ヒーター"
              className="mt-0.5 h-9 text-sm"
              data-testid="sku-import-basename"
            />
            <div className="mt-0.5 text-[10px] text-zinc-400 leading-snug">
              このシート全体を1つのルールとして扱います（連番 _001 は付きません）。
              空欄の場合はシート名から自動推定されます。
            </div>
          </div>
          <div>
            <Label className="text-xs">AI へのヒント（任意）</Label>
            <Input
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              placeholder="例: 1行目は品番ルール、以降は中分類コード表"
              className="mt-0.5 h-9 text-sm"
              data-testid="sku-import-hint"
            />
          </div>
          <Button
            onClick={analyze}
            disabled={analyzing || !chosenSheet}
            className="w-full bg-teal-700 hover:bg-teal-800"
            data-testid="sku-import-analyze-btn"
          >
            {analyzing ? (
              <><ArrowsClockwise className="animate-spin mr-1" size={14} />AI 解析中…</>
            ) : (
              <><Sparkle weight="fill" size={14} className="mr-1" />AI で解析 →</>
            )}
          </Button>
          {analyzing && (
            <AiProgressBanner
              steps={['シート内容を確認中…', 'Geminiで分類中…', 'マスターと突合中…', '差分プランを整形中…']}
              since={analyzeStartedAt}
              tone="indigo"
              defaultLabel="AIがSKU取り込みを解析しています…"
              footer="Excel/CSVの内容をAIが解析し、新規/更新/スキップに分類します（通常 20〜60 秒）。"
              testIdPrefix="sku-import-progress"
              simulatedStepMs={12000}
            />
          )}
        </div>
      )}

      {/* Diff plan */}
      {plan && (
        <div className="rounded-xl border border-indigo-200 bg-white overflow-hidden" data-testid="sku-import-plan">
          <div className="p-3 border-b bg-indigo-50/40">
            <div className="text-xs text-zinc-500 flex items-center gap-2">
              <MagnifyingGlass size={12} />
              <span className="truncate">{filename} / {plan.sheet_name}</span>
              <button onClick={resetSession} className="ml-auto text-rose-500 hover:underline text-[11px]">
                最初からやり直す
              </button>
            </div>
            {plan.summary && (
              <div className="mt-2 text-xs text-zinc-700 italic">{plan.summary}</div>
            )}
            <div className="mt-2 flex items-center gap-3 text-xs">
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5">
                新規 <strong>{plan.counts.create}</strong>
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5">
                更新 <strong>{plan.counts.update}</strong>
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-zinc-50 text-zinc-600 border border-zinc-200 px-2 py-0.5">
                スキップ <strong>{plan.counts.skip}</strong>
              </span>
              {plan.counts.error > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 px-2 py-0.5">
                  エラー <strong>{plan.counts.error}</strong>
                </span>
              )}
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-zinc-200" data-testid="sku-import-plan-items">
            {activeItems.map((it, i) => {
              const isOpen = expandedRow === i;
              const tone = ACTION_TONE[it.action] || ACTION_TONE.skip;
              const label = ACTION_LABEL[it.action] || it.action;
              const primary = it.data?.name || it.data?.code || `行 ${it.row_index}`;
              return (
                <div key={i} className="p-2" data-testid={`sku-import-item-${i}`}>
                  <button
                    onClick={() => setExpandedRow(isOpen ? null : i)}
                    className="w-full text-left flex items-center gap-2"
                  >
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full border text-[9px] font-semibold shrink-0 ${tone}`}>
                      {label}
                    </span>
                    <span className="text-[10px] text-zinc-500 shrink-0">
                      {TARGET_LABELS[it.target] || it.target}
                    </span>
                    <span className="text-sm font-semibold text-zinc-800 truncate flex-1">
                      {primary}
                    </span>
                    <ArrowRight size={12} className={`text-zinc-400 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="mt-2 rounded-lg bg-zinc-50 p-2 text-[10px] font-mono text-zinc-600 overflow-x-auto">
                      {it.reason && (
                        <div className="mb-1 text-zinc-500">
                          <Warning size={10} className="inline mr-1 text-amber-500" />
                          {it.reason}
                        </div>
                      )}
                      {it.error && (
                        <div className="mb-1 text-rose-700">
                          <XCircle size={10} className="inline mr-1" />
                          {it.error}
                        </div>
                      )}
                      {it.before && (
                        <div className="mb-1">
                          <div className="text-zinc-500 uppercase text-[9px]">変更前</div>
                          <pre className="whitespace-pre-wrap break-words">{JSON.stringify(it.before, null, 2)}</pre>
                        </div>
                      )}
                      {it.after && (
                        <div>
                          <div className="text-zinc-500 uppercase text-[9px]">
                            {it.action === 'create' ? '作成データ' : '変更後'}
                          </div>
                          <pre className="whitespace-pre-wrap break-words">{JSON.stringify(it.after, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="p-3 border-t bg-white flex gap-2">
            <Button
              variant="outline"
              onClick={resetSession}
              disabled={applying}
              className="flex-1"
              data-testid="sku-import-plan-cancel"
            >
              キャンセル
            </Button>
            <Button
              onClick={applyPlan}
              disabled={applying || !canApply}
              className="flex-1 bg-teal-700 hover:bg-teal-800"
              data-testid="sku-import-plan-apply"
            >
              {applying ? (
                <><ArrowsClockwise className="animate-spin mr-1" size={14} />適用中…</>
              ) : (
                <><CheckCircle weight="fill" size={14} className="mr-1" />適用する（{plan.counts.create + plan.counts.update}件）</>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Past imports — with undo */}
      {batches.length > 0 && (
        <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden" data-testid="sku-import-history">
          <div className="p-2 border-b bg-zinc-50">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              取り込み履歴（24時間以内なら取り消し可）
            </div>
          </div>
          <div className="divide-y divide-zinc-200 max-h-64 overflow-y-auto">
            {batches.slice(0, 15).map((b) => {
              const c = b.counts || {};
              const isUndone = b.status === 'undone';
              const isExpired = b.expires_at && new Date(b.expires_at) < new Date();
              const canUndo = !isUndone && !isExpired;
              return (
                <div key={b.id} className="p-2 flex items-center gap-2" data-testid={`sku-import-batch-${b.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-zinc-800 truncate">
                      {b.filename} <span className="text-zinc-400">/ {b.sheet_name}</span>
                    </div>
                    <div className="text-[10px] text-zinc-500 flex items-center gap-2">
                      <span>+{c.created || 0} / ~{c.updated || 0}{c.errors ? ` / ✕${c.errors}` : ''}</span>
                      <span>·</span>
                      <span>{formatDateTime(b.created_at)}</span>
                      {isUndone && <span className="text-rose-500 font-semibold">取消済</span>}
                      {!isUndone && isExpired && <span className="text-zinc-400">期限切れ</span>}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => undo(b.id)}
                    disabled={!canUndo || undoing === b.id}
                    className="h-7 px-2 text-[11px]"
                    data-testid={`sku-import-undo-${b.id}`}
                  >
                    {undoing === b.id ? (
                      <ArrowsClockwise className="animate-spin" size={11} />
                    ) : (
                      <><ArrowClockwise size={11} className="mr-1" />元に戻す</>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
